'use client'

import { useState } from 'react'
import NextLink from 'next/link'
import { cn } from '@/lib/utils'
import { errorsContain } from '@/lib/utils/common'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import Image from 'next/image'
import logo from '@/images/logo.svg'
import googleLogo from '@/images/integrations/google.svg'
import { Lock, Loader2 } from 'lucide-react'

export default function LoginForm({
  title,
  subtitle,
  loading,
  error,
  errors,
  errorCodes,
  ssoEnabled,
  onSubmit,
  onSSOLogin,
  onGoogleLogin,
  showFooterLinks,
  showEmailFallback,
  warningMessage,
  showProgress,
}) {
  const [emailFallbackOpen, setEmailFallbackOpen] = useState(false)

  const handleFormSubmit = e => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    onSubmit(formData.get('username'), formData.get('password'))
  }

  if (showProgress || (ssoEnabled === null && !ssoEnabled)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const emailPasswordForm = (
    <form onSubmit={handleFormSubmit} className="grid gap-4">
      <div className="grid gap-2">
        <label
          htmlFor="username"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Email
        </label>
        <input
          id="username"
          name="username"
          type="email"
          placeholder="name@company.com"
          autoComplete="email"
          required
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
            'file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            errorsContain(errorCodes, 'USER') &&
              'border-destructive focus-visible:ring-destructive',
          )}
        />
      </div>
      <div className="grid gap-2">
        <label htmlFor="password" className="text-sm font-medium leading-none">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            errorsContain(errorCodes, 'PASSWORD') &&
              'border-destructive focus-visible:ring-destructive',
          )}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className={cn(
          'inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2',
          'text-sm font-medium text-primary-foreground shadow',
          'transition-colors hover:bg-primary/90',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-50',
        )}
      >
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {loading ? 'Signing in...' : 'Sign in'}
      </button>
      {showFooterLinks && (
        <div className="flex items-center justify-between text-sm">
          <NextLink
            href="/register"
            className="text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
          >
            Create account
          </NextLink>
          <NextLink
            href="/forgot"
            className="text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
          >
            Forgot password?
          </NextLink>
        </div>
      )}
    </form>
  )

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left panel — brand */}
      <div className="relative hidden lg:flex flex-col justify-between bg-zinc-950 p-10 text-white">
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.03)_0%,transparent_50%)]" />
        <div className="relative flex items-center gap-2 text-lg font-medium">
          <Image src={logo} alt="" className="h-6 w-6 invert" />
          Tectra
        </div>
        <div className="relative space-y-2">
          <blockquote className="text-lg font-medium leading-relaxed">
            &ldquo;The platform that transformed how our team responds to
            critical incidents. Response times down 40%.&rdquo;
          </blockquote>
          <p className="text-sm text-zinc-400">Emergency Management Director</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex items-center justify-center p-8">
        <div className="mx-auto w-full max-w-[350px] space-y-6">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-2 lg:hidden">
            <Image src={logo} alt="" className="h-6 w-6" />
            <span className="text-lg font-medium">Tectra</span>
          </div>

          <div className="grid gap-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              {ssoEnabled ? 'Welcome back' : title}
            </h1>
            <p className="text-sm text-muted-foreground">
              {ssoEnabled ? 'Sign in to your organization' : subtitle}
            </p>
          </div>

          {warningMessage && (
            <div className="rounded-md border border-amber-200/50 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {warningMessage}
            </div>
          )}

          <ErrorAlerts
            errors={errors}
            error={error}
            message="There is an issue with your login attempt:"
          />

          {/* SSO layout */}
          {ssoEnabled === true && (
            <div className="grid gap-4">
              <button
                type="button"
                onClick={onSSOLogin}
                className={cn(
                  'inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2',
                  'text-sm font-medium text-primary-foreground shadow',
                  'transition-colors hover:bg-primary/90',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                <Lock className="h-3.5 w-3.5" />
                Sign in with SSO
              </button>

              {showEmailFallback && (
                <>
                  {!emailFallbackOpen ? (
                    <button
                      type="button"
                      onClick={() => setEmailFallbackOpen(true)}
                      className="text-sm text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
                    >
                      Use email instead
                    </button>
                  ) : (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            or
                          </span>
                        </div>
                      </div>
                      {emailPasswordForm}
                    </>
                  )}
                </>
              )}

              {!showEmailFallback && (
                <p className="text-center">
                  <NextLink
                    href="/admin-login"
                    className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
                  >
                    Admin access
                  </NextLink>
                </p>
              )}
            </div>
          )}

          {/* Standard layout */}
          {ssoEnabled === false && (
            <div className="grid gap-6">
              {onGoogleLogin && (
                <>
                  <button
                    type="button"
                    onClick={onGoogleLogin}
                    disabled={loading}
                    className={cn(
                      'inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2',
                      'text-sm font-medium shadow-sm',
                      'transition-colors hover:bg-accent hover:text-accent-foreground',
                      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                      'disabled:pointer-events-none disabled:opacity-50',
                    )}
                  >
                    <Image src={googleLogo} alt="" className="h-4 w-4" />
                    Continue with Google
                  </button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        or continue with
                      </span>
                    </div>
                  </div>
                </>
              )}
              {emailPasswordForm}
            </div>
          )}

          <p className="px-8 text-center text-xs text-muted-foreground">
            By continuing, you agree to our{' '}
            <span className="underline underline-offset-4 hover:text-foreground cursor-pointer">
              Terms of Service
            </span>{' '}
            and{' '}
            <span className="underline underline-offset-4 hover:text-foreground cursor-pointer">
              Privacy Policy
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}
